import { Button, cx } from "@light-technology/components";
import { useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  YAxis,
} from "recharts";
import { api } from "../../state/api";
import {
  CHART_AREA_COLORS,
  CHART_CURSOR_COLOR,
  CHART_TOP_MARGIN_PX,
  CHART_Y_AXIS_PADDING,
  hasNoChartData,
} from "../../utilities/analyticsHelpers";
import { ChartCard } from "./ChartCard";
import { FullHeightTooltipCursor } from "./ChartLines";
import { ChartTooltip } from "./ChartTooltip";
import { NoAccountsState } from "./NoAccountsState";
import "./charts.css";

type ActiveAccountsAreaChartProps = {
  appUUID: string;
  appSlug: string;
  className?: string;
};

export function ActiveAccountsAreaChart({
  appUUID,
  appSlug,
  className,
}: ActiveAccountsAreaChartProps) {
  const [focusedArea, setFocusedArea] = useState<string | null>(null);

  const {
    data: chartResponse,
    isLoading,
    error,
  } = api.useGetActiveAccountsChartQuery({
    appUUID,
  });

  const hasError = !!error;

  const chartData = chartResponse?.data ?? [];

  const planNames = chartResponse?.plan_names ?? [];

  const noData =
    !isLoading && !hasError && hasNoChartData(chartData, planNames);

  return (
    <ChartCard
      title="All-time active accounts"
      hasError={hasError}
      className={cx("flex flex-col", className)}
      isFullHeightChart
      action={
        !noData &&
        !hasError && (
          <Button
            color="link-gray"
            size="sm"
            href={`/apps/${appSlug}/accounts`}
            className="font-mono"
          >
            View accounts
          </Button>
        )
      }
    >
      {isLoading ? (
        <div className="mt-3xl flex min-h-62 flex-1 items-center justify-center">
          <div className="h-full w-full animate-pulse rounded bg-secondary" />
        </div>
      ) : noData ? (
        <NoAccountsState />
      ) : (
        <div className="flex flex-1 flex-col [&_*]:!outline-none [&_*:focus]:!outline-none">
          <ResponsiveContainer
            className="min-h-62 w-full"
            width="100%"
            height="100%"
          >
            <AreaChart
              data={chartData}
              margin={{
                top: CHART_TOP_MARGIN_PX,
                right: 0,
                left: 0,
                bottom: 1,
              }}
            >
              <defs>
                {CHART_AREA_COLORS.map((color, index) => (
                  <linearGradient
                    key={`gradient-${String(index)}`}
                    id={`areaGradient-${String(index)}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor={color.fill}
                      stopOpacity={0.8}
                    />
                    <stop offset="95%" stopColor={color.fill} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid
                strokeDasharray="0 0"
                className="[:last-child]:hidden"
                stroke="var(--color-border-tertiary)"
                vertical={false}
              />
              <YAxis
                domain={[
                  0,
                  (dataMax: number) => dataMax * CHART_Y_AXIS_PADDING,
                ]}
                hide
              />
              <Tooltip
                content={
                  <ChartTooltip
                    planNameColorOrder={planNames}
                    focusedItem={focusedArea}
                  />
                }
                cursor={
                  <FullHeightTooltipCursor
                    strokeDasharray="3 3"
                    stroke={CHART_CURSOR_COLOR}
                  />
                }
              />

              {CHART_AREA_COLORS.length > 0 &&
                (() => {
                  const names = planNames;
                  return names
                    .slice()
                    .reverse()
                    .map((planName, index) => {
                      const isFocused =
                        focusedArea === planName || focusedArea === null;

                      return (
                        <Area
                          key={`area-${planName}`}
                          type="basis"
                          dataKey={`plan_counts.${planName}`}
                          stackId="1"
                          stroke={
                            CHART_AREA_COLORS[index % CHART_AREA_COLORS.length]
                              .stroke
                          }
                          strokeWidth={isFocused ? 3 : 2}
                          strokeOpacity={isFocused ? 1 : 0.2}
                          fill={`url(#areaGradient-${String(index % CHART_AREA_COLORS.length)})`}
                          fillOpacity={isFocused ? 1 : 0.2}
                          dot={false}
                          activeDot={false}
                          animationDuration={500}
                          animationEasing="ease-out"
                          isAnimationActive={true}
                          zIndex={names.length + 1 - index}
                          onClick={() => {
                            setFocusedArea(
                              focusedArea === planName ? null : planName,
                            );
                          }}
                          style={{ cursor: "pointer" }}
                        />
                      );
                    });
                })()}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </ChartCard>
  );
}
